import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col } from 'react-bootstrap'
import { issueDestinationAPI } from '../api/issueDestinationAPI'
import { formatErrorResponse } from '../utils/errorHandlers'

function IssueDestinationSubcategoryCreateScreen() {
    const navigate = useNavigate()
    const location = useLocation()
    const [error, setError] = useState(null)
    const [destinations, setDestinations] = useState([])
    const [subcategory, setSubcategory] = useState({
        name: '',
        full_name: '',
        contact: '',
        bill_to_address: '',
        ship_to_address: '',
        phone: '',
        email: '',
        parent_destination: '',
        is_active: true
    })

    useEffect(() => {
        async function fetchDestinations() {
            try {
                const data = await issueDestinationAPI.getOptions()
                setDestinations(data)
                
                // Pre-select parent destination if passed from details screen
                const parentDestination = location.state?.parentDestination
                if (parentDestination) {
                    setSubcategory(prev => ({
                        ...prev,
                        parent_destination: parentDestination._id
                    }))
                }
            } catch (error) {
                console.error('Error fetching destinations:', error)
                setError('Failed to load destinations')
            }
        }
        fetchDestinations()
    }, [location.state])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            await issueDestinationAPI.subcategories.create(subcategory)
            
            // Navigate back to parent destination details if we came from there
            const parentDestination = location.state?.parentDestination
            if (parentDestination) {
                navigate(`/issue-destinations/${parentDestination._id}`)
            } else {
                navigate('/issue-destinations')
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleCancel = () => {
        const parentDestination = location.state?.parentDestination
        if (parentDestination) {
            navigate(`/issue-destinations/${parentDestination._id}`)
        } else {
            navigate('/issue-destinations')
        }
    }

    return (
        <Container className="py-4">
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <h1>Create Issue Destination Subcategory</h1>
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="parent_destination">
                            <Form.Label>Parent Destination*</Form.Label>
                            <Form.Select
                                value={subcategory.parent_destination}
                                onChange={(e) => setSubcategory({...subcategory, parent_destination: e.target.value})}
                                required
                            >
                                <option value="">Select parent destination...</option>
                                {destinations.map(dest => (
                                    <option key={dest._id} value={dest._id}>
                                        {dest.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="name">
                            <Form.Label>Name*</Form.Label>
                            <Form.Control
                                type="text"
                                value={subcategory.name}
                                onChange={(e) => setSubcategory({...subcategory, name: e.target.value})}
                                required
                                placeholder="Enter subcategory name"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="full_name">
                            <Form.Label>Full Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={subcategory.full_name}
                                onChange={(e) => setSubcategory({...subcategory, full_name: e.target.value})}
                                placeholder="Enter full name"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="contact">
                            <Form.Label>Contact</Form.Label>
                            <Form.Control
                                type="text"
                                value={subcategory.contact}
                                onChange={(e) => setSubcategory({...subcategory, contact: e.target.value})}
                                placeholder="Enter contact"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="bill_to_address">
                            <Form.Label>Bill To Address</Form.Label>
                            <Form.Control
                                type="text"
                                value={subcategory.bill_to_address}
                                onChange={(e) => setSubcategory({...subcategory, bill_to_address: e.target.value})}
                                placeholder="Enter billing address"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="ship_to_address">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">Ship To Address</Form.Label>
                                {subcategory.bill_to_address && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSubcategory({...subcategory, ship_to_address: subcategory.bill_to_address})}
                                        style={{ fontSize: '0.875rem', padding: '0' }}
                                    >
                                        <i className="bi bi-clipboard me-1"></i>
                                        Copy from Bill To
                                    </Button>
                                )}
                            </div>
                            <Form.Control
                                type="text"
                                value={subcategory.ship_to_address}
                                onChange={(e) => setSubcategory({...subcategory, ship_to_address: e.target.value})}
                                placeholder="Enter shipping address"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="phone">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="text"
                                value={subcategory.phone}
                                onChange={(e) => setSubcategory({...subcategory, phone: e.target.value})}
                                placeholder="Enter phone"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="email">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={subcategory.email}
                                onChange={(e) => setSubcategory({...subcategory, email: e.target.value})}
                                placeholder="Enter email"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}

                        <Button variant="success" type="submit" className="me-2">
                            Create
                        </Button>
                        <Button 
                            variant="outline-primary" 
                            onClick={handleCancel}
                            type="button"
                        >
                            <i className="fas fa-arrow-left me-2"></i>
                            Back to List
                        </Button>
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default IssueDestinationSubcategoryCreateScreen